import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import gtm from 'general/js/gtm';
import {
    getRoundById,
    getPlayerSitecoreData,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice,
} from 'general/js/helpers';
import { LazyImage } from 'project/lazy-image';
import { LiveEventSelect } from 'components/live-event-select';
import { LiveStatsCardContainer, statsNames } from 'components/live-stats';
import { playerSitecoreData, CHAMPION_CHOICE_ID } from './enums';

class LivePlayerStats extends React.Component {
    constructor(props) {
        super(props);
        this.roundStats = null;
        this.statsNames = [
            statsNames.FAIRWAY_HITS,
            statsNames.GREENS_IN_REGULATION,
            statsNames.AVERAGE_NUMBER_OF_PUTTS,
        ];

        this.state = {
            roundChoices: null,
            selectedRoundId: null,
            hasUserSelected: false,
        };
    }

    componentDidMount() {
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    setRound = (roundId) => {
        const { firstName, lastName } = this.getSitecoreData();

        gtm.push('Player Card', `Round Stats > ${roundId}`, `${firstName} ${lastName}`);
        this.setState({ selectedRoundId: roundId, hasUserSelected: true });
    }

    getSitecoreData() {
        return get(this.props, 'scoringPlayer.sitecoreData') || playerSitecoreData;
    }

    getRoundStats(id) {
        const { scoringStats } = this.props;

        if (!scoringStats) return null;

        return getRoundById(scoringStats.rounds, id);
    }

    getAvailableChoices() {
        const { liveScanner, scoringPlayer, scoringStats } = this.props;
        const roundChoices = get(liveScanner, 'liveComponents.stats.roundChoices');

        if (!Array.isArray(scoringStats.rounds)) return [];

        return cloneDeep(roundChoices).filter((choice) => {
            const roundStats = this.getRoundStats(choice.value);

            if (!roundStats) return false;

            return this.statsNames.some((statsName) => {
                if (!roundStats[statsName] ||
                    !Array.isArray(roundStats[statsName].stats)
                ) {
                    return false;
                }

                return roundStats[statsName].stats.some((statsItem) =>
                    statsItem.playerId.toString() === scoringPlayer.id.toString());
            });
        });
    }

    updateChoices() {
        const { liveScanner, scoringStats, isWithdrawn } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner || !scoringStats) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = this.getAvailableChoices();

        if (this.state.hasUserSelected &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(roundChoices, selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        // preselect CHAMPION round if player in CUT status or in any others
        if (isWithdrawn &&
            isRoundChoiceAvailable(roundChoices, CHAMPION_CHOICE_ID)
        ) {
            setSelectedChoice(roundChoices, CHAMPION_CHOICE_ID);
            this.setState({
                roundChoices,
                selectedRoundId: CHAMPION_CHOICE_ID,
                hasUserSelected: true,
            });
        }

        const foundChoice = roundChoices.find((choice) => choice.selected === true);

        if (!foundChoice && roundChoices.length) {
            const lastChoice = roundChoices[roundChoices.length - 1];

            lastChoice.selected = true;
            this.setState({
                roundChoices,
                selectedRoundId: lastChoice.value,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: (foundChoice && foundChoice.value) || null,
            hasUserSelected: false,
        });
    }

    getPlayerStats(statsName) {
        const { scoringPlayer } = this.props;
        const stats = get(this.roundStats, `${statsName}.stats`) || null;

        if (!stats) return null;

        const foundStats = stats.find((statsItem) => statsItem.playerId.toString() === scoringPlayer.id.toString());

        if (!foundStats) return null;

        return {
            playerId: scoringPlayer.id,
            value: foundStats.value,
            isPlayer: true,
        };
    }

    getLeadersCount(stats, value) {
        let count = 0;

        stats.forEach((statsItem) => {
            if (statsItem.value === value) {
                count += 1;
            }
        });

        return count;
    }

    getLeaderStats(statsName) {
        const { scoringPlayer, sitecorePlayers } = this.props;

        const stats = get(this.roundStats, `${statsName}.stats`) || null;

        if (!stats) return null;

        const statsItem = stats[0];
        const fisrtPlayerValue = statsItem.value;
        const leadersCount = this.getLeadersCount(stats, fisrtPlayerValue);

        // if curren player is leader do not show leader row
        if (scoringPlayer.id.toString() === statsItem.playerId.toString()) return null;

        const playerSD = getPlayerSitecoreData(sitecorePlayers, statsItem.playerId) || playerSitecoreData;
        let text = `Category leader (${playerSD.firstName.substr(0, 1).toUpperCase()}.${playerSD.lastName})`;

        if (leadersCount > 1) {
            text = `Category leader (${leadersCount} leaders)`;
        }

        return {
            statsId: 'Category',
            value: fisrtPlayerValue,
            text,
            isSingle: true
        };
    }

    getAverageStats(statsName) {
        if (!this.roundStats) return null;

        return {
            statsId: 'Average',
            value: this.roundStats[statsName].average || 0,
            text: 'Field average',
            isSingle: true
        };
    }

    getCard(statsName) {
        const { scoringStats } = this.props;
        const card = {
            name: statsName,
            stats: [],
            sitecoreData: scoringStats.sitecoreData[statsName]
        };
        const playerStats = this.getPlayerStats(statsName);
        const leaderStats = this.getLeaderStats(statsName);
        const averageStats = this.getAverageStats(statsName);

        if (!playerStats) return null;

        card.stats.push(playerStats);

        if (leaderStats !== null) {
            card.stats.push(leaderStats);
        }

        if (averageStats) {
            card.stats.push(this.getAverageStats(statsName));
        }

        return card;
    }

    getCards() {
        return this.statsNames
            .map((statName) => this.getCard(statName))
            .filter((card) => card !== null);
    }

    render() {
        const { scoringStats } = this.props;
        const { roundChoices } = this.state;

        if (!scoringStats ||
            !scoringStats.sitecoreData ||
            !roundChoices ||
            !roundChoices.length
        ) {
            return null;
        }

        this.roundStats = this.getRoundStats(this.state.selectedRoundId);

        const statsCards = this.getCards();

        if (!statsCards.length) return null;

        return (
            <div className="live-player-stats">
                <div className="live-player-stats__header">
                    {
                        roundChoices.length === 1 &&
                        <div className="single-round">{roundChoices[0].label}</div>
                    }
                    {
                        roundChoices.length > 1 &&
                        <div className="live-player-stats__dropdown">
                            <LiveEventSelect
                                choices={roundChoices}
                                onChange={this.setRound}
                            />
                        </div>
                    }
                    <div className="ntt-data-logo">
                        <LazyImage image={'/-/media/Patrons/nttdata-logo-white.png'} />
                    </div>
                </div>
                <div className="live-player-stats__list">
                    {
                        statsCards.map((statCard) =>
                            <LiveStatsCardContainer key={statCard.name} model={statCard} />)
                    }
                </div>
            </div>
        );
    }
}

LivePlayerStats.propTypes = {
    roundId: PropTypes.string,
    choices: PropTypes.array,
    scoringPlayer: PropTypes.object,
    scoringStats: PropTypes.object,
    setStatsRound: PropTypes.func,
    isWithdrawn: PropTypes.bool,
};

export default LivePlayerStats;
